import { FieldErrors } from 'react-hook-form'
import get from 'lodash/get'
import isNaN from 'lodash/isNaN'
import * as yup from 'yup'
import {
  DATE_OPTION_VALUES,
  DAYS_OF_WEEK_MESSAGE,
  EQUIPMENT_OPTION_VALUES,
  RATE_TYPE_OPTION_VALUES,
  REQUIRED_MESSAGE,
} from '../constants'
import { IEquipmentTypes, IDateTypes, IRateTypes, IAppTypes } from '../types'
import { isDaysOfWeekValid } from '../utils'
import { DaysOfWeekSchema } from './DaysOfWeekSchema'
import { LaneSchema, TLaneSchema } from './LaneSchema'
import { RecipientSchema, TRecipientSchema } from './RecipientSchema'

export const SavedLaneSchema = yup.object({
  /**
   * @example {
   *    pickup: { id: '123', text: 'Miami, FL' },
   *    pickupRadius: 50,
   *    dropoff: { id: '456', text: 'Austin, TX' },
   *    dropoffRadius: 25
   * }
   */
  lane: LaneSchema.required(REQUIRED_MESSAGE),
  /**
   * @example 'daysOfWeek'
   */
  dateType: yup
    .mixed<IDateTypes>()
    .oneOf(DATE_OPTION_VALUES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  /**
   * @example 'reefer'
   */
  equipment: yup
    .mixed<IEquipmentTypes>()
    .oneOf(EQUIPMENT_OPTION_VALUES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  /**
   * @example 'rpm'
   */
  rateType: yup
    .mixed<IRateTypes>()
    .oneOf(RATE_TYPE_OPTION_VALUES, REQUIRED_MESSAGE)
    .required(REQUIRED_MESSAGE),
  /**
   * @example 1200
   */
  rate: yup
    .number()
    .nullable()
    .when('rateType', {
      is: rateType => {
        return [IRateTypes.ratePerMile, IRateTypes.totalAllIn].includes(rateType)
      },
      then: yup
        .number()
        .transform(val => (isNaN(val) ? null : val))
        .required(REQUIRED_MESSAGE),
    }),
  /**
   * @example {
   * 'monday' : false,
   * 'tuesday' : false,
   * 'wednesday' : true,
   * 'thursday' : false,
   * 'friday' : true,
   * 'saturday' : false,
   * 'sunday' : true
   * }
   */
  daysOfWeek: DaysOfWeekSchema.nullable().when('dateType', {
    is: IDateTypes.daysOfWeek,
    then: DaysOfWeekSchema.test('days-valid', DAYS_OF_WEEK_MESSAGE, isDaysOfWeekValid),
  }),
  /**
   * @example 2023-01-01
   */
  date: yup
    .string()
    .when('dateType', {
      is: IDateTypes.specificDates,
      then: yup.string().required(REQUIRED_MESSAGE),
    })
    .nullable(),
  /**
   * @example '123'
   */
  motorCarrierId: yup
    .string()
    .nullable()
    .when('$app', {
      is: IAppTypes.fms,
      then: yup.string().required(REQUIRED_MESSAGE),
      otherwise: yup.string().nullable(),
    }),
  /**
   * @example false
   */
  receiveNotifications: yup.boolean(),
  /**
   * Array of recipients is only valid if app is marketplace
   * @example [
   *  { user: { id: '123', text: 'Jane Doe · janedoe@email.com', name: 'Jane Doe', email: 'janedoe@email.com }, receiveNotifications: true }
   * ]
   */
  recipients: yup.array<TRecipientSchema>().when('$app', {
    is: IAppTypes.marketplace,
    then: yup.array<TRecipientSchema>(RecipientSchema).required(REQUIRED_MESSAGE),
    otherwise: yup.array().max(0, 'There can only be a single recipient per saved lane'),
  }),
})

export type TSavedLaneSchema = yup.InferType<typeof SavedLaneSchema>

export const getErrorMessage = <T extends FieldErrors<TSavedLaneSchema>, K extends keyof T>(
  errors: T,
  keyName: K
): string | undefined => {
  return get(errors, [keyName, 'message'])
}

export const getLaneErrorMessage = <
  T extends FieldErrors<TSavedLaneSchema>,
  K extends keyof FieldErrors<TLaneSchema>
>(
  errors: T,
  keyName: K
): string | undefined => {
  const laneErrors = get(errors, 'lane')
  return get(laneErrors, [keyName, 'message'])
}
